import { createDeletedChatItem, createSentChatItem, type ServerChatItem } from './chatItem';
import { compareSortOrders } from './ids';
import { EntityStore } from './entityStore';
import type {
  ChatAttachment,
  ChatAuthor,
  ChatPreview,
  ChatReaction,
  EmojiId,
  WireAttachment,
  WireMediaVariant,
  WireMessage,
  WireReaction
} from './types';

/** Fallback author label when a referenced profile is absent from the entity store. */
export const PLACEHOLDER_AUTHOR_NAME = 'Unknown author';
/** Fallback CSS color when a referenced profile is absent from the entity store. */
export const PLACEHOLDER_AUTHOR_ACCENT = '#9ca3af';
/** Fallback reaction glyph when a referenced emoji is absent from the entity store. */
export const PLACEHOLDER_EMOJI_GLYPH = '?';

/** Projects one wire message into a server-backed render row using currently cached entities. */
export function projectMessage(wire: WireMessage, store: EntityStore): ServerChatItem {
  const author = projectAuthor(wire, store);

  if (wire.deleted) {
    return createDeletedChatItem({
      id: wire.id,
      sortOrder: wire.sortOrder,
      itemKey: wire.id,
      author,
      createdAt: wire.createdAt,
      ...(wire.rendererRow === undefined ? {} : { rendererRow: wire.rendererRow }),
      ...(wire.flags === undefined ? {} : { flags: wire.flags }),
      ...(wire.editedAt === undefined ? {} : { editedAt: wire.editedAt })
    });
  }

  return createSentChatItem({
    id: wire.id,
    sortOrder: wire.sortOrder,
    itemKey: wire.id,
    author,
    createdAt: wire.createdAt,
    ...(wire.rendererRow === undefined ? {} : { rendererRow: wire.rendererRow }),
    body: wire.body,
    ...(wire.bodyHtml === undefined ? {} : { bodyHtml: wire.bodyHtml }),
    ...(wire.flags === undefined ? {} : { flags: wire.flags }),
    ...(wire.containsOnlyAttachments === undefined
      ? {}
      : { containsOnlyAttachments: wire.containsOnlyAttachments }),
    ...(wire.containsOnlyEntities === undefined
      ? {}
      : { containsOnlyEntities: wire.containsOnlyEntities }),
    ...(wire.containsOnlyEmojiEntities === undefined
      ? {}
      : { containsOnlyEmojiEntities: wire.containsOnlyEmojiEntities }),
    attachments: projectAttachments(wire.attachments),
    previews: clonePreviews(wire.previews),
    reactions: projectReactions(wire.reactions, store),
    ...(wire.editedAt === undefined ? {} : { editedAt: wire.editedAt })
  });
}

/** Projects wire messages in input order; entity fallback behavior matches projectMessage. */
export function projectMessages(wires: readonly WireMessage[], store: EntityStore): ServerChatItem[] {
  return wires.map((wire) => projectMessage(wire, store));
}

function projectAuthor(wire: WireMessage, store: EntityStore): ChatAuthor {
  const profile = store.getProfile(wire.authorId);
  return {
    id: wire.authorId,
    name: profile?.name ?? PLACEHOLDER_AUTHOR_NAME,
    accent: profile?.accent ?? PLACEHOLDER_AUTHOR_ACCENT,
    ...(profile?.avatarUrl === undefined ? {} : { avatarUrl: profile.avatarUrl })
  };
}

function projectReactions(reactions: readonly WireReaction[], store: EntityStore): ChatReaction[] {
  const groups = new Map<
    EmojiId,
    {
      count: number;
      minSortOrder: WireReaction['sortOrder'];
      reactedByMe: boolean;
    }
  >();

  for (const reaction of reactions) {
    const current = groups.get(reaction.emojiId);
    if (!current) {
      groups.set(reaction.emojiId, {
        count: 1,
        minSortOrder: reaction.sortOrder,
        reactedByMe: reaction.profileId === store.viewerProfileId
      });
      continue;
    }

    groups.set(reaction.emojiId, {
      count: current.count + 1,
      minSortOrder:
        compareSortOrders(reaction.sortOrder, current.minSortOrder) < 0
          ? reaction.sortOrder
          : current.minSortOrder,
      reactedByMe: current.reactedByMe || reaction.profileId === store.viewerProfileId
    });
  }

  return [...groups.entries()]
    .sort(([leftEmojiId, left], [rightEmojiId, right]) => {
      const order = compareSortOrders(left.minSortOrder, right.minSortOrder);
      if (order !== 0) return order;
      return leftEmojiId < rightEmojiId ? -1 : leftEmojiId > rightEmojiId ? 1 : 0;
    })
    .map(([emojiId, group]) => ({
      emoji: store.getEmoji(emojiId)?.glyph ?? PLACEHOLDER_EMOJI_GLYPH,
      count: group.count,
      ...(group.reactedByMe ? { reactedByMe: true } : {})
    }));
}

function projectAttachments(attachments: readonly WireAttachment[]): ChatAttachment[] {
  return attachments.flatMap((attachment): ChatAttachment[] => {
    const kind = kindForContentType(attachment.contentType);
    if (kind === 'file') {
      return [
        {
          id: attachment.id,
          kind,
          url: attachment.originalUrl,
          originalUrl: attachment.originalUrl,
          fileName: attachment.fileName,
          sizeBytes: attachment.sizeBytes,
          ...(attachment.thumbhash === undefined ? {} : { thumbhash: attachment.thumbhash })
        }
      ];
    }

    const displayVariant = pickDisplayVariant(attachment);
    if (!displayVariant) return [];

    const srcSet = srcSetFor(attachment);
    return [
      {
        id: attachment.id,
        kind,
        url: displayVariant.url,
        ...(srcSet === undefined ? {} : { srcSet }),
        width: displayVariant.width,
        height: displayVariant.height,
        originalUrl: attachment.originalUrl,
        fileName: attachment.fileName,
        sizeBytes: attachment.sizeBytes,
        ...(attachment.thumbhash === undefined ? {} : { thumbhash: attachment.thumbhash })
      }
    ];
  });
}

function pickDisplayVariant(attachment: WireAttachment): WireMediaVariant | undefined {
  return attachment.variants?.medium ?? attachment.variants?.large;
}

function srcSetFor(attachment: WireAttachment): string | undefined {
  const { medium, large } = attachment.variants ?? {};
  if (!medium || !large) return undefined;
  return `${medium.url} ${medium.width}w, ${large.url} ${large.width}w`;
}

function kindForContentType(contentType: string): ChatAttachment['kind'] {
  if (contentType.startsWith('image/')) return 'image';
  if (contentType.startsWith('video/')) return 'video';
  return 'file';
}

function clonePreviews(previews: readonly ChatPreview[]): ChatPreview[] {
  return previews.map((preview) => ({
    ...preview,
    ...(preview.image === undefined ? {} : { image: { ...preview.image } })
  }));
}
